import type { Term, Tone } from "@/lib/campaign";
import type { Firm } from "@/lib/types";

/**
 * Firms (ADR-045 in `platform`), in words: the four statuses and what a change of one does, the
 * form's values and the arithmetic to `POST /firms` and `PUT /firms/{id}`, the tag rules the
 * resolver applies (`normalize_tag`), what the Firms page's search matches, and the order a state's
 * firms are shown in (ADR-170). Pure, for `test/firms.test.ts`.
 */

export const FIRM_STATUSES = ["active", "not_started", "paused", "done"] as const;
export type FirmStatus = (typeof FIRM_STATUSES)[number];

const STATUS: Record<FirmStatus, Term & { tone: Tone }> = {
  active: { label: "Active", description: "Its leads are worked on the calling campaign.", tone: "success" },
  not_started: {
    label: "Not started",
    description: "Signed up, not yet live: its leads are held, and new ones are refused at enlist.",
    tone: "outline",
  },
  paused: {
    label: "Paused",
    description: "Every list row of its leads is held, with the reason on each; new leads are refused.",
    tone: "warning",
  },
  done: { label: "Done", description: "The engagement ended. Its leads are held for good.", tone: "neutral" },
};

export function firmStatusText(status: string): Term & { tone: Tone } {
  return STATUS[status as FirmStatus] ?? { label: status, description: "Not a status this console knows.", tone: "outline" };
}

/** Whether a status stops the firm's dialing (`INACTIVE_STATUSES` in `models/firms.py`). */
export function stopsDialing(status: string): boolean {
  return status !== "active";
}

/** A tag as the resolver keeps it: lowercase, trimmed, spaces and dashes as underscores. */
export function normalizeTag(raw: string): string {
  return raw.trim().toLowerCase().replaceAll(/[\s-]+/g, "_");
}

export const TAG_RE = /^[a-z0-9][a-z0-9_]{0,79}$/;

/** "ca, tx" -> ["CA", "TX"]; a problem for anything that is not two letters. */
export function parseStates(text: string): { states: string[]; problem: string | null } {
  const states = [...new Set(text.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter(Boolean))];
  const bad = states.find((s) => !/^[A-Z]{2}$/.test(s));
  return { states, problem: bad ? `${bad} is not a two-letter state` : null };
}

/** "[CA] Larry H. Parker": the label the sheet's convention gives, offered as a default. */
export function suggestedLabel(name: string, states: readonly string[]): string {
  const prefix = states.length === 0 ? "" : states.length > 2 ? "[Multi] " : `[${states.join(", ")}] `;
  return `${prefix}${name.trim()}`;
}

/** The fifty states and DC, as the state filter lists them (`US_STATES` in platform's `agencies.py`). */
export const US_STATES: Readonly<Record<string, string>> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky",
  LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island",
  SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

/** "California (CA)"; a code this list does not know, as it is. */
export function stateName(code: string): string {
  const name = US_STATES[code];
  return name ? `${name} (${code})` : code;
}

/** A contact's State list (ACL-576): every state by its name, "California (CA)", in the name's order. */
export const STATE_CHOICES: readonly { key: string; label: string }[] = Object.keys(US_STATES).map((code) => ({
  key: code,
  label: stateName(code),
}));

/** The highest rank a state priority takes (`FIRM_PRIORITY_MAX` in core-api's `schemas.py`). */
export const PRIORITY_MAX = 99;

type Ranked = Pick<Firm, "label" | "states" | "state_priorities">;

/** The firm's rank in `state` (ADR-170), 1 first; null when it does not take the state or has no rank there. */
export function stateRank(firm: Pick<Firm, "states" | "state_priorities">, state: string): number | null {
  if (!firm.states.includes(state)) return null;
  const rank = firm.state_priorities?.[state];
  return typeof rank === "number" ? rank : null;
}

/** The firms that take cases in `state`, the first one first: by rank, then the unranked ones, each by label. */
export function firmsInState<F extends Ranked>(firms: readonly F[], state: string): F[] {
  const rankOf = (f: F) => stateRank(f, state) ?? Number.POSITIVE_INFINITY;
  return firms
    .filter((f) => f.states.includes(state))
    .sort((a, b) => {
      const ra = rankOf(a);
      const rb = rankOf(b);
      return ra === rb ? a.label.localeCompare(b.label) : ra < rb ? -1 : 1;
    });
}

/** The state's preferred firms: those with its lowest rank, usually one. An unranked firm is never preferred. */
export function preferredIn<F extends Ranked & { id: number }>(firms: readonly F[], state: string): Set<number> {
  const ranks = firms.map((f) => stateRank(f, state)).filter((r): r is number => r !== null);
  if (ranks.length === 0) return new Set();
  const first = Math.min(...ranks);
  return new Set(firms.filter((f) => stateRank(f, state) === first).map((f) => f.id));
}

/**
 * A firm picker's options for a contact in `state`: the firms that take cases there, in their order,
 * and every other firm in the order given (the server's, by label). No usable state, every firm is
 * an "other".
 */
export function firmChoices<F extends Ranked>(
  firms: readonly F[],
  state: string | null | undefined,
): { state: string | null; inState: F[]; others: F[] } {
  const code = (state ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return { state: null, inState: [], others: [...firms] };
  const inState = firmsInState(firms, code);
  return { state: code, inState, others: firms.filter((f) => !inState.includes(f)) };
}

/**
 * The firms a person may name on a new contact (ACL-644, platform ADR-208): a supervisor any firm of
 * the tenant; a rep the active ones only, since core-api refuses them a firm that takes no new leads.
 */
export function firmsToName<F extends { status: string }>(firms: readonly F[], supervisor: boolean): F[] {
  return supervisor ? [...firms] : firms.filter((f) => f.status === "active");
}

/** A typed priority's problem, or null: empty (no rank) or a whole number from 1 to 99. */
export function priorityProblem(text: string | undefined): string | null {
  const v = (text ?? "").trim();
  if (!v) return null;
  return /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= PRIORITY_MAX ? null : `1 to ${PRIORITY_MAX}, or empty`;
}

/** The typed priorities as `state_priorities`: only for `states`, only the ones filled in. */
export function prioritiesFrom(typed: Readonly<Record<string, string | undefined>> | undefined, states: readonly string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const state of states) {
    const v = (typed?.[state] ?? "").trim();
    if (v && priorityProblem(v) === null) out[state] = Number(v);
  }
  return out;
}

export interface FirmValues {
  code: string;
  name: string;
  label: string;
  states: string;
  /** Each state's typed rank (ADR-170), "" for none. */
  state_priorities: Record<string, string>;
  languages: string[];
  status: string;
  pause_reason: string;
  /** The one of the firm's tags its agreements are filed under in the contract service (ADR-154 in
   *  platform); empty sends the contact's own tag. */
  contract_tag: string;
}

export const NEW_FIRM: FirmValues = {
  code: "",
  name: "",
  label: "",
  states: "",
  state_priorities: {},
  languages: ["en"],
  status: "active",
  pause_reason: "",
  contract_tag: "",
};

export function firmValues(firm: Firm): FirmValues {
  const contractTag = firm.settings.contract_tag;
  return {
    code: firm.code,
    name: firm.name,
    label: firm.label,
    states: firm.states.join(", "),
    state_priorities: Object.fromEntries(firm.states.map((s) => [s, String(stateRank(firm, s) ?? "")])),
    languages: [...firm.languages],
    status: firm.status,
    pause_reason: firm.pause_reason ?? "",
    contract_tag: typeof contractTag === "string" && firm.tags.some((t) => t.tag === contractTag) ? contractTag : "",
  };
}

export interface FirmInput {
  code?: string;
  name?: string;
  label?: string;
  states?: string[];
  state_priorities?: Record<string, number>;
  languages?: string[];
  status?: string;
  pause_reason?: string | null;
  settings?: Record<string, unknown>;
  tags?: string[];
  tenant_id?: number | null;
}

/** What `POST /firms` takes: the code normalised, the states parsed, the label defaulted. */
export function newFirmPayload(values: FirmValues): FirmInput {
  const { states } = parseStates(values.states);
  const ranks = prioritiesFrom(values.state_priorities, states);
  return {
    code: normalizeTag(values.code),
    name: values.name.trim(),
    label: values.label.trim() || suggestedLabel(values.name, states),
    states,
    ...(Object.keys(ranks).length > 0 && { state_priorities: ranks }),
    languages: values.languages,
    status: values.status,
    pause_reason: values.pause_reason.trim() || null,
  };
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** The fields an edit changed, as `PUT /firms/{id}` takes them, and nothing else. */
export function firmChanges(values: FirmValues, firm: Firm): FirmInput {
  const before = firmValues(firm);
  const out: FirmInput = {};
  if (values.name.trim() !== firm.name) out.name = values.name.trim();
  if (values.label.trim() !== firm.label) out.label = values.label.trim();
  const { states } = parseStates(values.states);
  if (!sameList(states, firm.states)) out.states = states;
  // The whole map, as `PUT` takes it; core-api drops the rank of a state taken off the firm itself.
  const ranks = prioritiesFrom(values.state_priorities, states);
  const savedRanks = prioritiesFrom(before.state_priorities, firm.states);
  const rankKeys = new Set([...Object.keys(ranks), ...Object.keys(savedRanks)]);
  if ([...rankKeys].some((k) => ranks[k] !== savedRanks[k])) out.state_priorities = ranks;
  if (!sameList(values.languages, firm.languages)) out.languages = values.languages;
  if (values.status !== firm.status) out.status = values.status;
  if (values.pause_reason.trim() !== before.pause_reason) out.pause_reason = values.pause_reason.trim() || null;
  const settings: Record<string, unknown> = {};
  // Emptied, it goes as "", which core-api drops: the contact's own tag goes again.
  if (values.contract_tag !== before.contract_tag) settings.contract_tag = values.contract_tag;
  if (Object.keys(settings).length > 0) out.settings = { ...firm.settings, ...settings };
  return out;
}

/** The sentence above Save when the status change holds or releases the firm's leads. */
export function statusWarning(from: string, to: string, contacts: number): string | null {
  if (from === to) return null;
  const n = contacts.toLocaleString();
  if (stopsDialing(to) && !stopsDialing(from)) {
    return `Saving holds every list row of this firm's ${n} contacts in the same commit, with the reason on each, and refuses new leads for it at enlist. Nothing retires while it waits.`;
  }
  if (!stopsDialing(to) && stopsDialing(from)) {
    return `Saving releases exactly the holds this firm's pause put on its ${n} contacts. A hold a live booking gave stays.`;
  }
  return `Its ${n} contacts stay held; only the word changes.`;
}

/**
 * Whether a firm matches what was typed in the Firms page's search, by the server's rule for a firm's own
 * columns (`firms.word_matches`, ADR-101): every word is in its label, name or code, or in one of its tags,
 * in any case. The server also looks in the firm fields an admin marked searchable; this is what the page
 * can show at once, while it asks.
 */
export function firmMatches(firm: Pick<Firm, "name" | "label" | "code" | "tags">, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const texts = [firm.label, firm.name, firm.code, ...firm.tags.map((t) => t.tag)].map((s) => s.toLowerCase());
  return words.every((word) => texts.some((text) => text.includes(word)));
}
